<template>
  <div class="control-panel">
    <div class="container mt-4">
      <div class="panel-header">
        <h1>Control Panel</h1>
        <p>User & Database Management System</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p>Loading user data...</p>
      </div>

      <!-- Content when user data is loaded -->
      <div v-else>
        <!-- User Statistics -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title mb-0">User Management</h3>
          </div>
          <div class="card-body">
            <p>This is the control panel for managing users, databases and permissions.</p>
            
            <div class="user-stats">
              <div class="stat-card">
                <h4>Your Role</h4>
                <p>{{ userRolesText }}</p>
              </div>
              <div class="stat-card">
                <h4>Your Database</h4>
                <p>{{ getDatabaseDescription(user.preferred_db) || 'Default' }}</p>
              </div>
              <div class="stat-card">
                <h4>Username</h4>
                <p>{{ user.username || 'N/A' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- User Management Actions -->
        <div class="card mt-4">
          <div class="card-header">
            <h3 class="card-title mb-0">User Management</h3>
          </div>
          <div class="card-body">
            <div class="actions-grid">
              <button class="btn btn-primary" @click="getAllUsers">
                <i class="fas fa-sync-alt me-2"></i>
                Refresh Users
              </button>
              <button class="btn btn-success" @click="showAddUserModal = true">
                <i class="fas fa-user-plus me-2"></i>
                Add New User
              </button>
              <button class="btn btn-info" @click="refreshDatabases">
                <i class="fas fa-database me-2"></i>
                Refresh Databases
              </button>
            </div>
          </div>
        </div>

        <!-- Users List -->
        <div class="card mt-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3 class="card-title mb-0">Users List</h3>
            <span class="badge bg-primary">{{ users.length }} users</span>
          </div>
          <div class="card-body">
            <div v-if="users.length === 0" class="text-center py-4">
              <p class="text-muted">No users found</p>
              <button class="btn btn-primary" @click="getAllUsers">
                Load Users
              </button>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Database</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>{{ user.id }}</td>
                    <td>
                      <strong>{{ user.username }}</strong>
                      <span v-if="user.id === currentUser.id" class="badge bg-info ms-1">You</span>
                    </td>
                    <td>{{ user.email }}</td>
                    <td>
                      <span v-if="user.roles && user.roles.length > 0" class="badge bg-primary me-1">
                        {{ user.roles.join(', ') }}
                      </span>
                      <span v-else class="badge bg-secondary">No roles</span>
                    </td>
                    <td>
                      <span class="badge" :class="getDatabaseBadgeClass(user.preferred_db)">
                        {{ getDatabaseDescription(user.preferred_db) || 'Default' }}
                      </span>
                    </td>
                    <td>
                      <span v-if="user.isActive" class="badge bg-success">Active</span>
                      <span v-else class="badge bg-danger">Inactive</span>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button 
                          class="btn btn-outline-primary" 
                          @click="editUser(user)"
                          title="Edit User"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button 
                          class="btn btn-outline-danger" 
                          @click="confirmRemoveUser(user)"
                          :disabled="user.id === currentUser.id"
                          title="Remove User"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Add User Modal -->
        <div v-if="showAddUserModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Add New User</h5>
                <button type="button" class="btn-close" @click="closeAddUserModal"></button>
              </div>
              <div class="modal-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Username *</label>
                      <input type="text" class="form-control" v-model="newUser.username" required>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Email *</label>
                      <input type="email" class="form-control" v-model="newUser.email" required>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Password *</label>
                      <input type="password" class="form-control" v-model="newUser.password" required>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Roles</label>
                      <select class="form-select" v-model="newUser.roles" multiple>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                      <div class="form-text">Hold Ctrl to select multiple roles</div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Default Database</label>
                      <select class="form-select" v-model="newUser.preferred_db">
                        <option value="">Select Database</option>
                        <option v-for="db in availableDatabases" :key="db.name" :value="db.name">
                          {{ db.label }}
                        </option>
                      </select>
                      <div class="form-text">Default database for this user</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeAddUserModal">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" @click="addNewUser" :disabled="addingUser">
                  <i class="fas fa-user-plus me-2"></i>
                  {{ addingUser ? 'Adding...' : 'Add User' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit User Modal -->
        <div v-if="showEditUserModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Edit User: {{ editingUser.username }}</h5>
                <button type="button" class="btn-close" @click="closeEditUserModal"></button>
              </div>
              <div class="modal-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Username</label>
                      <input type="text" class="form-control" v-model="editingUser.username" disabled>
                      <div class="form-text">Username cannot be changed</div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Email</label>
                      <input type="email" class="form-control" v-model="editingUser.email">
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Status</label>
                      <select class="form-select" v-model="editingUser.isActive">
                        <option :value="true">Active</option>
                        <option :value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Roles</label>
                      <select class="form-select" v-model="editingUser.roles" multiple>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                      <div class="form-text">Hold Ctrl to select multiple roles</div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">Default Database</label>
                      <select class="form-select" v-model="editingUser.preferred_db">
                        <option value="">Select Database</option>
                        <option v-for="db in availableDatabases" :key="db.name" :value="db.name">
                          {{ db.label }}
                        </option>
                      </select>
                      <div class="form-text">User will automatically login to this database</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeEditUserModal">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" @click="updateUser" :disabled="updatingUser">
                  <i class="fas fa-save me-2"></i>
                  {{ updatingUser ? 'Updating...' : 'Update User' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Remove User Modal -->
        <div v-if="showRemoveUserModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Remove User</h5>
                <button type="button" class="btn-close" @click="closeRemoveUserModal"></button>
              </div>
              <div class="modal-body">
                <div v-if="userToRemove" class="text-center">
                  <div class="alert alert-warning">
                    <h5>Confirm Removal</h5>
                    <p>Are you sure you want to remove user:</p>
                    <p><strong>{{ userToRemove.username }}</strong> ({{ userToRemove.email }})</p>
                    <p class="text-danger">This action cannot be undone!</p>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeRemoveUserModal">
                  Cancel
                </button>
                <button type="button" class="btn btn-danger" @click="removeUser" :disabled="removingUser">
                  <i class="fas fa-trash me-2"></i>
                  {{ removingUser ? 'Removing...' : 'Yes, Remove User' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Alert Messages -->
        <div v-if="alert.message" :class="['alert', alert.type === 'success' ? 'alert-success' : 'alert-danger']" role="alert">
          <i :class="alert.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
          {{ alert.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '@/services/api';

export default {
  name: 'ControlPanel',
  
  data() {
    return {
      loading: false,
      addingUser: false,
      updatingUser: false,
      removingUser: false,
      users: [],
      availableDatabases: [],
      showAddUserModal: false,
      showEditUserModal: false,
      showRemoveUserModal: false,
      userToRemove: null,
      editingUser: null,
      newUser: {
        username: '',
        email: '',
        password: '',
        roles: ['user'],
        preferred_db: 'db2'
      },
      alert: {
        message: '',
        type: ''
      }
    };
  },
  
  computed: {
    ...mapGetters('auth', ['currentUser']),
    
    user() {
      return this.currentUser;
    },
    
    userRolesText() {
      if (!this.user || !this.user.roles) return 'No roles assigned';
      return Array.isArray(this.user.roles) ? this.user.roles.join(', ') : 'No roles assigned';
    }
  },
  
  methods: {
    async getAllUsers() {
      try {
        this.loading = true;
        console.log('📋 Fetching users from backend...');
        
        // جلب المستخدمين من الـ backend
        const response = await api.get('/auth/users');
        this.users = response.data.users || response.data;
        
        // جلب الداتابيز المتاحة
        await this.loadAvailableDatabases();
        
        this.showAlert('Users loaded successfully!', 'success');
        console.log('✅ Users loaded from backend:', this.users);
        
      } catch (error) {
        console.error('Error fetching users from backend:', error);
        this.showAlert('Error loading users: ' + (error.response?.data?.message || error.message), 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadAvailableDatabases() {
      try {
        const response = await api.get('/db/databases');
        this.availableDatabases = response.data.databases;
        console.log('✅ Databases loaded:', this.availableDatabases);
      } catch (error) {
        console.error('Error loading databases:', error);
        this.availableDatabases = [
          { name: 'db1', label: 'Mostorod25 (Production)' },
          { name: 'db2', label: 'aytest (Test)' }
        ];
      }
    },

    async refreshDatabases() {
      await this.loadAvailableDatabases();
      this.showAlert('Databases list refreshed!', 'success');
    },
    
    async addNewUser() {
      // التحقق من البيانات
      if (!this.newUser.username || !this.newUser.email || !this.newUser.password) {
        this.showAlert('Please fill all required fields', 'error');
        return;
      }

      try {
        this.addingUser = true;
        console.log('➕ Adding new user to backend:', this.newUser);
        
        // إرسال البيانات للـ backend
        const response = await api.post('/auth/register', this.newUser);

        this.showAlert(`User "${this.newUser.username}" added successfully!`, 'success');
        this.closeAddUserModal();
        
        // إعادة تحميل قائمة المستخدمين
        this.getAllUsers();
        
      } catch (error) {
        console.error('Error adding user to backend:', error);
        const errorMessage = error.response?.data?.message || 'Error adding user';
        this.showAlert(errorMessage, 'error');
      } finally {
        this.addingUser = false;
      }
    },

    async updateUser() {
      if (!this.editingUser) return;

      try {
        this.updatingUser = true;
        console.log('✏️ Updating user:', this.editingUser);
        
        // تحديث بيانات المستخدم - استخدم PUT بدل POST
        const response = await api.put(`/auth/users/${this.editingUser.id}`, {
          email: this.editingUser.email,
          roles: this.editingUser.roles,
          isActive: this.editingUser.isActive,
          preferred_db: this.editingUser.preferred_db
        });

        console.log('✅ Update response:', response.data);
        this.showAlert(`User "${this.editingUser.username}" updated successfully!`, 'success');
        this.closeEditUserModal();
        
        // إعادة تحميل قائمة المستخدمين
        this.getAllUsers();
        
      } catch (error) {
        console.error('❌ Error updating user:', error);
        const errorMessage = error.response?.data?.message || error.response?.data || 'Error updating user';
        this.showAlert(`Update failed: ${errorMessage}`, 'error');
      } finally {
        this.updatingUser = false;
      }
    },

    async editUser(user) {
      try {
        console.log('🔄 Loading user details for editing:', user.id);
        
        // نجيب بيانات اليوزر بالتفصيل من الـ API
        const response = await api.get(`/auth/users/${user.id}`);
        this.editingUser = { ...response.data.user };
        
        // نتأكد إن الـ roles بتكون array
        if (typeof this.editingUser.roles === 'string') {
          this.editingUser.roles = this.editingUser.roles.split(', ').filter(role => role.trim());
        }
        
        this.showEditUserModal = true;
        
      } catch (error) {
        console.error('Error loading user details:', error);
        this.showAlert('Error loading user details', 'error');
      }
    },
    
    async removeUser() {
      if (!this.userToRemove) return;

      try {
        this.removingUser = true;
        console.log('🗑️ Removing user from backend:', this.userToRemove.username);
        
        // حذف المستخدم من الـ backend
        await api.delete(`/auth/users/${this.userToRemove.id}`);
        
        this.showAlert(`User "${this.userToRemove.username}" removed successfully!`, 'success');
        this.closeRemoveUserModal();
        
        // إعادة تحميل قائمة المستخدمين
        this.getAllUsers();
        
      } catch (error) {
        console.error('Error removing user from backend:', error);
        const errorMessage = error.response?.data?.message || 'Error removing user';
        this.showAlert(errorMessage, 'error');
      } finally {
        this.removingUser = false;
      }
    },
    
    confirmRemoveUser(user) {
      // منع حذف المستخدم الحالي
      if (user.id === this.user.id) {
        this.showAlert('You cannot remove your own account!', 'error');
        return;
      }
      
      this.userToRemove = user;
      this.showRemoveUserModal = true;
    },
    
    closeAddUserModal() {
      this.showAddUserModal = false;
      this.resetNewUserForm();
    },

    closeEditUserModal() {
      this.showEditUserModal = false;
      this.editingUser = null;
    },
    
    closeRemoveUserModal() {
      this.showRemoveUserModal = false;
      this.userToRemove = null;
    },
    
    resetNewUserForm() {
      this.newUser = {
        username: '',
        email: '',
        password: '',
        roles: ['user'],
        preferred_db: 'db2'
      };
    },

    getDatabaseName(dbKey) {
      const dbMap = {
        'db1': 'Mostorod25',
        'db2': 'aytest'
      };
      return dbMap[dbKey] || dbKey || 'Default';
    },
    getDatabaseDescription(dbKey) {
  const db = this.availableDatabases.find(d => d.name === dbKey);
  return db?.description || db?.label || dbKey;
},

    getDatabaseBadgeClass(dbKey) {
      const classMap = {
        'db1': 'bg-success',
        'db2': 'bg-warning text-dark'
      };
      return classMap[dbKey] || 'bg-secondary';
    },
    
    showAlert(message, type) {
      this.alert = { message, type };
      setTimeout(() => {
        this.alert.message = '';
      }, 5000);
    }
  },
  
  mounted() {
    console.log('🔧 ControlPanel mounted - User:', this.user);
    // تحميل المستخدمين تلقائياً عند فتح الصفحة
    this.getAllUsers();
  }
}
</script>

<style scoped>
/* نفس الـ styles السابقة مع إضافات */
.control-panel {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px 0;
}

.panel-header {
  margin-bottom: 30px;
  text-align: center;
}

.panel-header h1 {
  color: #333;
  margin-bottom: 10px;
  font-weight: 700;
}

.panel-header p {
  color: #666;
  font-size: 18px;
}

.loading-container {
  text-align: center;
  color: #666;
  padding: 50px 0;
}

.card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  background: white;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 15px 20px;
  color: #333;
}

.card-title {
  font-weight: 600;
  margin: 0;
  color: #333;
}

.card-body {
  padding: 20px;
}

.user-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.stat-card {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  border-left: 4px solid #007bff;
}

.stat-card h4 {
  color: #666;
  margin-bottom: 10px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-card p {
  color: #007bff;
  font-weight: bold;
  font-size: 16px;
  margin: 0;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 15px;
}

.btn {
  padding: 10px 15px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-outline-primary {
  color: #007bff;
  border-color: #007bff;
  background-color: transparent;
}

.btn-outline-danger {
  color: #dc3545;
  border-color: #dc3545;
  background-color: transparent;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

.table {
  margin-bottom: 0;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #333;
}

.badge {
  font-size: 12px;
  padding: 4px 8px;
}

.modal-content {
  border: none;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 15px 20px;
}

.modal-title {
  font-weight: 600;
  color: #333;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 15px 20px;
}

.form-label {
  font-weight: 500;
  color: #333;
  margin-bottom: 5px;
}

.form-control, .form-select {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
}

.form-control:focus, .form-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.alert {
  border-radius: 6px;
  padding: 10px 15px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .control-panel {
    padding: 10px;
  }
  
  .user-stats {
    grid-template-columns: 1fr;
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
  }
  
  .card-body {
    padding: 15px;
  }
  
  .table-responsive {
    font-size: 14px;
  }
  
  .btn-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
}
</style>